import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Send, MessageSquare, ArrowLeft } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { QILogo } from "@/components/site/QILogo";
import { toast } from "sonner";

const fmt = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const ConvList = ({ convs, active, onPick }) => (
  <div className="overflow-y-auto">
    {convs.length === 0 && <p className="p-6 text-sm text-white/40" data-testid="chat-empty-list">No conversations yet. Open a QI's profile or the Partners page and tap "Chat".</p>}
    {convs.map((c) => (
      <button key={c.conversation_id} onClick={() => onPick(c.conversation_id)} data-testid={`chat-conv-${c.conversation_id}`}
        className={`w-full text-left flex items-center gap-3 px-4 py-4 border-b border-white/5 transition-colors ${active === c.conversation_id ? "bg-white/[0.07]" : "hover:bg-white/[0.04]"}`}>
        <QILogo profile={c.other} size="w-11 h-11" text="text-sm" />
        <div className="min-w-0 flex-1">
          <p className="font-bold text-sm truncate">{c.other.company_name || "Member"}{c.other.kind === "partner" && <span className="ml-1.5 text-[9px] font-black uppercase text-qi-pink">Partner</span>}</p>
          <p className="text-xs text-white/45 truncate">{c.last_message || "Say hello"}</p>
        </div>
        {c.unread > 0 && <span className="bg-qi-pink text-white text-[10px] font-black rounded-full px-2 py-0.5">{c.unread}</span>}
      </button>
    ))}
  </div>
);

const Thread = ({ convId, onBack, onSent }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const scroller = useRef();

  useEffect(() => {
    let alive = true;
    const load = () => api.get(`/chat/conversations/${convId}/messages`).then(({ data: d }) => { if (alive) setData(d); }).catch(() => {});
    load();
    const t = setInterval(load, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [convId]);

  useEffect(() => {
    if (scroller.current) {
      scroller.current.scrollTop = scroller.current.scrollHeight;
    }
  }, [data?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data: msg } = await api.post(`/chat/conversations/${convId}/messages`, { body: text });
      setData({ ...data, messages: [...data.messages, msg] });
      setText("");
      onSent();
    } catch (ex) {
      toast.error(errMsg(ex));
    }
  };

  if (!data) return <div className="flex-1" />;
  return (
    <div className="flex-1 flex flex-col min-w-0" data-testid="chat-thread">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
        <button onClick={onBack} className="md:hidden text-white/60" data-testid="chat-back-button"><ArrowLeft size={18} /></button>
        <QILogo profile={data.other} size="w-10 h-10" text="text-sm" />
        <div className="min-w-0">
          <p className="font-bold truncate">{data.other.company_name}</p>
          {data.other.kind === "partner" && <p className="text-xs text-white/50">{data.other.contact_name} · {data.other.profession}</p>}
          {data.other.slug && <Link to={`/qi/${data.other.slug}`} className="text-xs text-qi-pink">View profile</Link>}
        </div>
      </div>
      <div ref={scroller} className="flex-1 overflow-y-auto p-5 space-y-3">
        {data.messages.length === 0 && <p className="text-center text-sm text-white/35 mt-10">Start the conversation.</p>}
        {data.messages.map((m) => {
          const mine = m.sender_id === user.user_id;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${mine ? "bg-qi-pink text-white rounded-br-md" : "bg-white/[0.07] text-white rounded-bl-md"}`} data-testid="chat-message">
                <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                <p className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-white/35"}`}>{fmt(m.created_at)}</p>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="p-4 border-t border-white/10 flex gap-3">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a message..." className="field" data-testid="chat-message-input" />
        <button className="btn-pink !px-4" data-testid="chat-message-send-button"><Send size={16} /></button>
      </form>
    </div>
  );
};

export default function Chat() {
  const [params, setParams] = useSearchParams();
  const active = params.get("c");
  const [convs, setConvs] = useState([]);

  useEffect(() => {
    const load = () => api.get("/chat/conversations").then(({ data }) => setConvs(data)).catch(() => {});
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [active]);

  const reload = () => api.get("/chat/conversations").then(({ data }) => setConvs(data)).catch(() => {});

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="chat-page">
      <span className="eyebrow">The 1031 Club</span>
      <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-3 mb-6">Messages</h1>
      <div className="glass-card overflow-hidden flex h-[70vh]">
        <div className={`${active ? "hidden md:flex" : "flex"} flex-col w-full md:w-80 border-r border-white/10`}>
          <ConvList convs={convs} active={active} onPick={(id) => setParams({ c: id })} />
        </div>
        {active ? <Thread key={active} convId={active} onBack={() => setParams({})} onSent={reload} /> : (
          <div className="hidden md:flex flex-1 items-center justify-center text-white/35 flex-col gap-3">
            <MessageSquare size={36} />
            <p className="text-sm">Select a conversation</p>
          </div>
        )}
      </div>
    </div>
  );
}
